import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { IPAMStatCard } from './IPAMStatCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Network, Plus, Edit, Trash2, Copy, CheckCircle, AlertTriangle, Wifi, Monitor,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkDeleteBar from '../../components/BulkDeleteBar';

const initialFormData = {
  subnet_id: '',
  ip_address: '',
  hostname: '',
  mac_address: '',
  status: 'Disponible',
  device_type: '',
  notes: ''
};

const statusOptions = ['Disponible', 'Usado', 'Reservado', 'DHCP'];
const deviceTypeOptions = ['Server', 'Switch', 'Firewall', 'Printer', 'Workstation', 'Router', 'AP', 'Otro'];

const AddressesPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('ipam.addresses');
  const [addresses, setAddresses] = useState([]);
  const [subnets, setSubnets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Bulk selection
  const {
    selectedIds,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleRow,
    toggleAll,
    clearSelection
  } = useBulkSelection(addresses);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'ip_address', dir: 'asc' });
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    search: '',
    subnet_id: '',
    status: ''
  });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, disponible: 0, usado: 0, reservado: 0, dhcp: 0 });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchSubnets = async () => {
    try {
      const response = await api.get('/ipam/subnets/all');
      setSubnets(response.data || []);
    } catch (error) {
      console.error('Error loading subnets:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/ipam/addresses/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      
      if (columnFilters.search) params.append('search', columnFilters.search);
      if (columnFilters.subnet_id && columnFilters.subnet_id !== 'all') {
        params.append('subnet_id', columnFilters.subnet_id);
      }
      if (columnFilters.status && columnFilters.status !== 'all') {
        params.append('status', columnFilters.status);
      }

      const response = await api.get(`/ipam/addresses?${params}`);
      setAddresses(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar Direcciones IP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubnets(); fetchStats(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAddresses(); }, [pagination.page, sortConfig]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchAddresses();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setCopyingFrom(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (address) => {
    setEditingItem(address);
    setCopyingFrom(null);
    setFormData({
      subnet_id: address.subnet_id?.toString() || '',
      ip_address: address.ip_address || '',
      hostname: address.hostname || '',
      mac_address: address.mac_address || '',
      status: address.status || 'Disponible',
      device_type: address.device_type || '',
      notes: address.notes || ''
    });
    setModalOpen(true);
  };

  const handleCopy = (address) => {
    setCopyingFrom(address.ip_address);
    setEditingItem(null);
    setFormData({
      subnet_id: address.subnet_id?.toString() || '',
      ip_address: '', // Unique - empty
      hostname: '', // Unique - empty
      mac_address: '', // Unique - empty
      status: address.status || 'Disponible',
      device_type: address.device_type || '',
      notes: address.notes || ''
    });
    setCopyModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.subnet_id || !formData.ip_address.trim()) {
      toast.error('Subnet e IP son requeridos');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        subnet_id: parseInt(formData.subnet_id),
        hostname: formData.hostname || null,
        mac_address: formData.mac_address || null,
        device_type: formData.device_type || null,
        notes: formData.notes || null
      };

      if (editingItem) {
        await api.put(`/ipam/addresses/${editingItem.id}`, payload);
        toast.success('Dirección IP actualizada');
      } else {
        await api.post('/ipam/addresses', payload);
        toast.success('Dirección IP creada');
      }

      setModalOpen(false);
      setCopyModalOpen(false);
      setEditingItem(null);
      setCopyingFrom(null);
      setFormData(initialFormData);
      fetchAddresses();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/ipam/addresses/${deleteConfirm.id}`);
      toast.success('Dirección IP eliminada');
      setDeleteConfirm(null);
      fetchAddresses();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Status badge colors
  const getStatusBadge = (status) => {
    const colors = {
      'Disponible': 'bg-green-500/10 text-green-600 dark:text-green-400',
      'Usado': 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      'Reservado': 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      'DHCP': 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
    };
    return colors[status] || 'bg-muted text-muted-foreground';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="addresses-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wifi className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Direcciones IP</h1>
              <p className="text-sm text-muted-foreground">Gestión de direccionamiento IP</p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} data-testid="create-address-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva IP
            </Button>
          )}
        </div>

        {/* Stats Cards - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <IPAMStatCard icon={Wifi} value={stats.total} label="Total IPs"
            selected={!columnFilters.status || columnFilters.status === 'all'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, status: '' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={CheckCircle} value={stats.disponible} label="Disponibles" iconClassName="text-green-500"
            selected={columnFilters.status === 'Disponible'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, status: 'Disponible' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={Monitor} value={stats.usado} label="Usadas" iconClassName="text-blue-500"
            selected={columnFilters.status === 'Usado'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, status: 'Usado' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={AlertTriangle} value={stats.reservado} label="Reservadas" iconClassName="text-yellow-500"
            selected={columnFilters.status === 'Reservado'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, status: 'Reservado' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={Network} value={stats.dhcp} label="DHCP" iconClassName="text-purple-500"
            selected={columnFilters.status === 'DHCP'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, status: 'DHCP' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
        </div>

        {/* Table View */}
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full table-subtle">
            <thead>
              <tr className="border-b border-border/50">
                {canDelete && (
                  <th className="w-10 p-3">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Seleccionar todos"
                      className={isSomeSelected ? 'opacity-50' : ''}
                    />
                  </th>
                )}
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('ip_address')}
                >
                  <div className="flex items-center gap-1">
                    IP
                    <SortIcon field="ip_address" />
                  </div>
                </th>
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('hostname')}
                >
                  <div className="flex items-center gap-1">
                    HOSTNAME
                    <SortIcon field="hostname" />
                  </div>
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  SUBNET
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  MAC
                </th>
                <th 
                  className="text-center p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center justify-center gap-1">
                    ESTADO
                    <SortIcon field="status" />
                  </div>
                </th>
                <th className="w-28 text-right p-3 font-medium text-xs uppercase tracking-wider">
                  ACCIONES
                </th>
              </tr>
              <tr className="border-b border-border/30 bg-muted/30">
                {canDelete && <th className="p-2"></th>}
                <th className="p-2">
                  <Input
                    placeholder="Buscar IP/Host/MAC..."
                    value={columnFilters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="h-8 text-xs"
                  />
                </th>
                <th className="p-2"></th>
                <th className="p-2">
                  <Select value={columnFilters.subnet_id} onValueChange={(v) => handleFilterChange('subnet_id', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {subnets.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.network}/{s.mask}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </th>
                <th className="p-2"></th>
                <th className="p-2">
                  <Select value={columnFilters.status} onValueChange={(v) => handleFilterChange('status', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {statusOptions.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {loading ? (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="text-center p-8 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : addresses.length === 0 ? (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="text-center p-8">
                    <Wifi className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground">No hay Direcciones IP</p>
                  </td>
                </tr>
              ) : (
                addresses.map((addr) => (
                  <tr key={addr.id} className={`hover:bg-muted/30 ${isSelected(addr.id) ? 'bg-primary/5' : ''}`} data-testid={`address-row-${addr.id}`}>
                    {canDelete && (
                      <td className="p-3 w-10">
                        <Checkbox
                          checked={isSelected(addr.id)}
                          onCheckedChange={() => toggleRow(addr.id)}
                          aria-label={`Seleccionar IP ${addr.ip_address}`}
                        />
                      </td>
                    )}
                    <td className="p-3">
                      <span className="font-mono font-bold text-primary">{addr.ip_address}</span>
                    </td>
                    <td className="p-3">
                      {addr.hostname || <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted font-mono">
                        {addr.network}/{addr.mask}
                      </span>
                    </td>
                    <td className="p-3">
                      {addr.mac_address ? (
                        <span className="font-mono text-xs">{addr.mac_address}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(addr.status)}`}>
                        {addr.status}
                      </span>
                    </td>
                    <td className="p-3 w-28">
                      <div className="flex justify-end gap-0.5">
                        {canUpdate && (
                          <button onClick={() => handleEdit(addr)} className="action-btn action-btn-edit" title="Editar">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canCreate && (
                          <button onClick={() => handleCopy(addr)} className="action-btn action-btn-copy" title="Copiar">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => setDeleteConfirm(addr)} className="action-btn-delete" title="Eliminar">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
              <span className="text-sm text-muted-foreground">
                {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Página {pagination.page} de {pagination.pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Dirección IP' : 'Nueva Dirección IP'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Subnet *</Label>
                <Select value={formData.subnet_id} onValueChange={(v) => handleInputChange('subnet_id', v)}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Seleccionar subnet" />
                  </SelectTrigger>
                  <SelectContent>
                    {subnets.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.network}/{s.mask} {s.description && `- ${s.description}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Dirección IP *</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => handleInputChange('ip_address', e.target.value)}
                  placeholder="192.168.1.100"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div>
                <Label>Hostname</Label>
                <Input
                  value={formData.hostname}
                  onChange={(e) => handleInputChange('hostname', e.target.value)}
                  placeholder="server01.domain.local"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>MAC Address</Label>
                <Input
                  value={formData.mac_address}
                  onChange={(e) => handleInputChange('mac_address', e.target.value)}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estado</Label>
                  <Select value={formData.status} onValueChange={(v) => handleInputChange('status', v)}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo Dispositivo</Label>
                  <Select value={formData.device_type} onValueChange={(v) => handleInputChange('device_type', v)}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Sin tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin tipo</SelectItem>
                      {deviceTypeOptions.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Notas adicionales"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editingItem ? 'Guardar' : 'Crear')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar Dirección IP
              </DialogTitle>
              {copyingFrom && <p className="text-sm text-muted-foreground">Copiando desde: {copyingFrom}</p>}
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Campos únicos (obligatorio)</p>
              </div>
              <div>
                <Label>Dirección IP *</Label>
                <Input value={formData.ip_address} onChange={(e) => handleInputChange('ip_address', e.target.value)} className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label>Hostname</Label>
                <Input value={formData.hostname} onChange={(e) => handleInputChange('hostname', e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>MAC Address</Label>
                <Input value={formData.mac_address} onChange={(e) => handleInputChange('mac_address', e.target.value)} className="mt-1.5 font-mono" />
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Campos copiados</p>
              </div>
              <div>
                <Label>Subnet</Label>
                <Select value={formData.subnet_id} onValueChange={(v) => handleInputChange('subnet_id', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {subnets.map((s) => (<SelectItem key={s.id} value={String(s.id)}>{s.network}/{s.mask}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estado</Label>
                  <Select value={formData.status} onValueChange={(v) => handleInputChange('status', v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={formData.device_type} onValueChange={(v) => handleInputChange('device_type', v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Sin tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin tipo</SelectItem>
                      {deviceTypeOptions.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Input value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Creando...' : 'Crear Copia'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar dirección IP <strong>{deleteConfirm?.ip_address}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Bar */}
        {canDelete && (
          <BulkDeleteBar
            selectedIds={selectedIds}
            onClear={clearSelection}
            onSuccess={() => { fetchAddresses(); fetchStats(); }}
            endpoint="/ipam/addresses"
            entityName="IP"
          />
        )}
      </div>
    </Layout>
  );
};

export default AddressesPage;
